import { useRef, useState, useEffect } from 'react';
import backgroundNoiseUrl from '../assets/office_background_noise.wav';
import { getMicErrorType } from '../themed/micError';

// VAD Threshold - This value might need tuning based on environment and microphone sensitivity.
// A higher value means more silence will be filtered out.
const VAD_THRESHOLD = 0.005; // Example: 0.005 for quiet environments, 0.01-0.03 for noisier ones.
// Background loop audio URL - place your audio file in public/ and set the path here.
const BACKGROUND_LOOP_URL = backgroundNoiseUrl;
const noop = () => {};

export default function useVoiceBotCall(
  socketUrl,
  startAnimation,
  stopAnimation,
  setIsPrimarySpeaking,
  enableBackgroundNoise,
  disableNoiseCancellation,
  sampleRate
) {
  // State variables for UI feedback
  const [isMuted, setIsMuted] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [isCallConnected, setIsCallConnected] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callError, setCallError] = useState(null); // 'micBlocked' | 'noMic' | 'unknown' | null

  // Refs to hold mutable objects that don't trigger re-renders
  // Mirrors isCalling so socket handlers and unmount cleanup (which keep the
  // closure of the render that created them) see the current value.
  const isCallingRef = useRef(false);
  const socketRef = useRef(null); // WebSocket connection
  const audioCtxRef = useRef(null); // Web Audio API AudioContext
  const micStreamRef = useRef(null); // Microphone MediaStream
  const scriptNodeRef = useRef(null); // ScriptProcessorNode for mic processing (deprecated, but used for direct control)
  const isMutedRef = useRef(isMuted);

  // Playback queue for incoming audio from the bot
  const playbackQueue = useRef([]); // Stores decoded Float32Array audio chunks
  const isPlayingAudio = useRef(false); // Flag to prevent multiple playback loops
  const currentPlaybackSource = useRef(null); // Reference to the currently playing AudioBufferSourceNode

  // Background connecting tone (HTMLAudioElement) ref
  const backgroundAudioRef = useRef(null);

  // New ref to track if the first audio chunk has been sent
  const isFirstAudioChunkSentRef = useRef(false);
  // New ref to ensure we emit 'playback_complete' only once per idle period
  const hasEmittedStoppedPlayingRef = useRef(false);

  // Call duration tracking
  const callStartTimeRef = useRef(null); // Timestamp when call started
  const durationIntervalRef = useRef(null); // Interval for updating duration

  /**
   * Calculates the Root Mean Square (RMS) of an audio buffer.
   * This is a simple measure of the average power of the audio signal,
   * used here for basic Voice Activity Detection (VAD).
   * @param {Float32Array} buffer - The audio data buffer (e.g., from microphone input).
   * @returns {number} - The RMS value, a non-negative number.
   */
  const calculateRMS = (buffer) => {
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      sum += buffer[i] * buffer[i]; // Sum of squares
    }
    return Math.sqrt(sum / buffer.length); // Square root of the mean of squares
  };

  /**
   * Decodes base64 encoded PCM16 audio data into a Float32Array.
   * This is necessary for Web Audio API processing.
   * @param {string} base64 - Base64 encoded PCM16 audio string.
   * @returns {Float32Array} - Decoded audio samples as Float32Array.
   */
  const decodePCM16 = (base64) => {
    const binary = atob(base64); // Decode base64 to binary string
    const len = binary.length / 2; // Each sample is 2 bytes (16-bit)
    const floatBuffer = new Float32Array(len); // Create a Float32Array for Web Audio API

    for (let i = 0; i < len; i++) {
      // Read 16-bit little-endian value
      const lo = binary.charCodeAt(i * 2);
      const hi = binary.charCodeAt(i * 2 + 1);
      let value = (hi << 8) | lo; // Combine bytes to form 16-bit integer

      // Handle signed 16-bit integer (two's complement)
      if (value & 0x8000) {
        // If the most significant bit is set, it's a negative number
        value = value - 0x10000; // Convert to negative value
      }
      // Normalize to -1.0 to 1.0 range for Float32Array
      floatBuffer[i] = value / 32768.0; // 32768 is 2^15, max value for signed 16-bit is 32767
    }
    return floatBuffer;
  };

  /**
   * Schedules and plays the next audio chunk(s) from the playback queue.
   * This function ensures sequential playback by chaining 'onended' events.
   */
  const scheduleNextPlayback = () => {
    // If no audio context or queue is empty, stop playback and reset flags
    if (!audioCtxRef.current || playbackQueue.current.length === 0) {
      isPlayingAudio.current = false;
      stopAnimation();
      currentPlaybackSource.current = null;
      // Emit a one-time 'playback_complete' event when playback becomes idle
      if (
        !hasEmittedStoppedPlayingRef.current &&
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        try {
          socketRef.current.send(
            JSON.stringify({ event_type: 'playback_complete' })
          );
          hasEmittedStoppedPlayingRef.current = true;
        } catch (e) {
          // Swallow send errors; connection cleanup will handle state
          // Intentionally no console noise to avoid log spam
        }
      }
      return;
    }

    // Concatenate all available chunks in the queue into a single buffer
    // This reduces the overhead of creating many small AudioBufferSourceNodes
    const totalLength = playbackQueue.current.reduce(
      (acc, chunk) => acc + chunk.length,
      0
    );
    const combinedBuffer = new Float32Array(totalLength);
    let offset = 0;
    while (playbackQueue.current.length > 0) {
      const chunk = playbackQueue.current.shift(); // Take chunks from the front of the queue
      combinedBuffer.set(chunk, offset); // Copy chunk data to the combined buffer
      offset += chunk.length;
    }

    // Create an AudioBuffer from the combined audio data
    const audioBuffer = audioCtxRef.current.createBuffer(
      1, // mono channel
      combinedBuffer.length, // total number of samples
      audioCtxRef.current.sampleRate // Use the AudioContext's sample rate (8000 Hz)
    );
    audioBuffer.getChannelData(0).set(combinedBuffer); // Set the audio data to the buffer

    // Create an AudioBufferSourceNode to play the audioBuffer
    const source = audioCtxRef.current.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioCtxRef.current.destination); // Connect to speakers

    // When this source finishes playing, schedule the next one
    source.onended = () => {
      scheduleNextPlayback(); // Recursively call to play the next available audio
    };

    source.start(0); // Start playing immediately (at time 0 of the audio context)
    currentPlaybackSource.current = source; // Store reference to the current source
    isPlayingAudio.current = true; // Set flag to indicate audio is playing
    // Reset the idle guard since playback resumed
    hasEmittedStoppedPlayingRef.current = false;
    startAnimation();
  };

  /**
   * Starts the background loop audio (e.g., ringback tone) while connecting.
   */
  const startBackgroundLoop = () => {
    try {
      // Avoid creating multiple elements
      if (!BACKGROUND_LOOP_URL) return;
      const playPromise = backgroundAudioRef.current.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {
          // Autoplay may be blocked until a user gesture; silently ignore
        });
      }
    } catch (e) {
      noop();
    }
  };

  /**
   * Stops the background loop audio if playing.
   */
  const stopBackgroundLoop = () => {
    const bg = backgroundAudioRef.current;
    if (bg) {
      try {
        bg.pause();
        bg.currentTime = 0;
      } catch (e) {
        noop();
      }
    }
  };

  /**
   * Handles incoming 'audio_reply' messages from the WebSocket.
   * Decodes the audio and adds it to the playback queue.
   * @param {object} data - The parsed JSON data from the WebSocket message.
   */
  const handleAudioReply = (data) => {
    const floatBuffer = decodePCM16(data.audio_b64); // Decode the base64 audio
    playbackQueue.current.push(floatBuffer); // Add to the queue

    // If audio is not currently playing, start the playback chain
    // Otherwise, the new chunk will be picked up by the ongoing playback chain
    if (!isPlayingAudio.current) {
      scheduleNextPlayback();
    }
  };

  /**
   * Handles an 'interrupt' event from the server.
   * Clears the playback queue and stops any currently playing audio.
   */
  const handleInterrupt = () => {
    console.log('Received interrupt event. Clearing audio buffer.');
    playbackQueue.current = []; // Clear the queue

    if (currentPlaybackSource.current) {
      currentPlaybackSource.current.stop(); // Stop current playback
      currentPlaybackSource.current.disconnect(); // Disconnect to release resources
      currentPlaybackSource.current = null;
    }
    isPlayingAudio.current = false; // Reset playback flag
    stopAnimation();
    // Emit 'playback_complete' once if not already emitted for this idle period
    if (
      !hasEmittedStoppedPlayingRef.current &&
      socketRef.current &&
      socketRef.current.readyState === WebSocket.OPEN
    ) {
      try {
        socketRef.current.send(
          JSON.stringify({ event_type: 'playback_complete' })
        );
        hasEmittedStoppedPlayingRef.current = true;
      } catch (e) {
        noop();
      }
    }
  };

  /**
   * General WebSocket message handler.
   * Parses incoming messages and dispatches them to appropriate handlers.
   * @param {MessageEvent} event - The WebSocket message event.
   */
  const handleSocketMessage = (event) => {
    try {
      const parsed = JSON.parse(event.data);
      if (
        parsed.event_type === 'audio_reply' &&
        parsed.audio_format === 'pcm'
      ) {
        handleAudioReply(parsed);
      } else if (parsed.event_type === 'call_status') {
        // Optional: Log or handle call status messages from the bot
        console.log('Call status:', parsed.status);
      } else if (parsed.event_type === 'interrupt') {
        handleInterrupt();
      }
    } catch (e) {
      console.error('Failed to parse socket message:', e);
    }
  };

  /**
   * Starts the call duration timer
   */
  const startCallTimer = () => {
    callStartTimeRef.current = Date.now();
    setCallDuration(0);

    // Update duration every second
    durationIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor(
        (Date.now() - callStartTimeRef.current) / 1000
      );
      setCallDuration(elapsed);
    }, 1000);
  };

  /**
   * Stops the call duration timer
   */
  const stopCallTimer = () => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    callStartTimeRef.current = null;
  };

  /**
   * Initiates the voice call: sets up AudioContext, WebSocket, and microphone input.
   */
  const startCall = async (url) => {
    if (isCallingRef.current) return; // Prevent starting multiple calls
    isCallingRef.current = true;
    setIsCalling(true);
    setCallError(null);
    isFirstAudioChunkSentRef.current = false; // Reset for a new call
    hasEmittedStoppedPlayingRef.current = false; // Reset idle guard for new call

    try {
      // Initialize AudioContext with the desired sample rate (8000 Hz)
      audioCtxRef.current = new (window.AudioContext ||
        window.webkitAudioContext)({ sampleRate });

      // Initialize WebSocket connection
      socketRef.current = new WebSocket(url || socketUrl);
      // socketRef.current.binaryType is not set as we send JSON string

      // Set up WebSocket event listeners
      socketRef.current.onmessage = handleSocketMessage;
      socketRef.current.onopen = () => {
        console.log('WebSocket connected.');
        setIsCallConnected(true);
        startCallTimer();
        // Start background noise only after the socket connection is established
        if (enableBackgroundNoise) {
          startBackgroundLoop();
        }
        // Optionally send an initial message to the bot to signal call start
        // This is separate from the first audio chunk 'start' event
        // socketRef.current.send(JSON.stringify({ event_type: 'call_initiated' }));
      };
      socketRef.current.onclose = () => {
        console.log('WebSocket disconnected.');
        stopCall(); // Clean up resources if connection closes unexpectedly
      };
      socketRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        stopCall(); // Clean up on WebSocket error
      };

      // Request microphone access from the user
      const stream = disableNoiseCancellation
        ? await navigator.mediaDevices.getUserMedia({ audio: true })
        : await navigator.mediaDevices.getUserMedia({
            audio: {
              sampleRate,
              channelCount: 1,
              noiseSuppression: true,
              echoCancellation: true,
              autoGainControl: false,
            },
          });
      // The call was ended while the permission prompt was open: release the mic.
      if (!isCallingRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      micStreamRef.current = stream;

      // Create audio nodes for processing microphone input
      const source = audioCtxRef.current.createMediaStreamSource(stream);

      if (!disableNoiseCancellation) {
        // High-pass filter to remove low hum (<80Hz)
        const highpass = audioCtxRef.current.createBiquadFilter();
        highpass.type = 'highpass';
        highpass.frequency.value = 80;

        // Compressor to even out levels (softly)
        const compressor = audioCtxRef.current.createDynamicsCompressor();
        compressor.threshold.setValueAtTime(
          -50,
          audioCtxRef.current.currentTime
        );
        compressor.knee.setValueAtTime(40, audioCtxRef.current.currentTime);
        compressor.ratio.setValueAtTime(4, audioCtxRef.current.currentTime);
        compressor.attack.setValueAtTime(0.01, audioCtxRef.current.currentTime);
        compressor.release.setValueAtTime(0.3, audioCtxRef.current.currentTime);

        source.connect(highpass);
        highpass.connect(compressor);
      }

      // ScriptProcessorNode is deprecated, but provides direct audio buffer access.
      // For new projects, consider AudioWorkletNode for more robust processing.
      const scriptNode = audioCtxRef.current.createScriptProcessor(4096, 1, 1); // Buffer size, input channels, output channels

      // This event handler is called when the audio buffer is ready for processing
      scriptNode.onaudioprocess = (e) => {
        // Only send data if WebSocket is open and not muted
        if (
          !socketRef.current ||
          socketRef.current.readyState !== WebSocket.OPEN ||
          isMutedRef.current
        ) {
          return;
        }

        // Get audio data from the microphone input buffer
        const input = e.inputBuffer.getChannelData(0);

        // Calculate RMS for Voice Activity Detection (VAD)
        const rms = calculateRMS(input);

        // Only send audio data if RMS is above the threshold (voice activity detected)
        if (rms > VAD_THRESHOLD) {
          setIsPrimarySpeaking(true);
        } else {
          setIsPrimarySpeaking(false);
          // console.log('Silence detected, not sending (RMS:', rms.toFixed(4), ')'); // Uncomment for debugging VAD
        }
        let currentEventType;
        if (!isFirstAudioChunkSentRef.current) {
          currentEventType = 'start';
          isFirstAudioChunkSentRef.current = true; // Mark as sent
        } else {
          currentEventType = 'audio_chunk';
        }

        // Convert Float32Array (Web Audio API format) to PCM16 (Int16Array)
        const pcmDataLength = input.length * 2; // 2 bytes per sample for 16-bit
        const pcmBuffer = new ArrayBuffer(pcmDataLength);
        const pcmView = new DataView(pcmBuffer);

        for (let i = 0; i < input.length; i++) {
          // Clamp the float value to -1.0 to 1.0 and scale to 16-bit integer range
          let s = Math.max(-1, Math.min(1, input[i]));
          pcmView.setInt16(i * 2, s * 0x7fff, true); // 0x7fff = 32767 (max signed 16-bit value), true for little-endian
        }

        // Convert ArrayBuffer to a string of binary data, then Base64 encode it
        const binaryString = String.fromCharCode.apply(
          null,
          new Uint8Array(pcmBuffer)
        );
        const base64Audio = btoa(binaryString);

        // Create the JSON payload with the requested keys and dynamic event_type
        const payload = {
          event_type: currentEventType,
          audio_format: 'pcm',
          audio_b64: base64Audio,
        };

        // Send the JSON payload as a string over the WebSocket
        socketRef.current.send(JSON.stringify(payload));
      };

      // Connect the audio nodes: Microphone -> ScriptProcessorNode -> AudioContext destination (speakers)
      // Connecting scriptNode to destination allows you to hear your own mic input, which can be useful for debugging.
      source.connect(scriptNode);
      scriptNode.connect(audioCtxRef.current.destination);
      scriptNodeRef.current = scriptNode;
    } catch (error) {
      console.error('Error starting call:', error);
      // Ignore failures of a call the user already ended.
      if (isCallingRef.current) setCallError(getMicErrorType(error));
      // Full cleanup: the socket opened above must not stay connected.
      stopCall();
    }
  };

  /**
   * Stops the voice call and cleans up all associated resources.
   */
  const stopCall = () => {
    if (!isCallingRef.current) return;
    isCallingRef.current = false;

    setIsCalling(false);
    setIsCallConnected(false);
    isPlayingAudio.current = false; // Reset playback flag
    stopAnimation();
    if (enableBackgroundNoise) {
      stopBackgroundLoop();
    }
    isFirstAudioChunkSentRef.current = false; // Reset for next call
    stopCallTimer();

    // Stop and disconnect the currently playing audio source from the bot
    if (currentPlaybackSource.current) {
      currentPlaybackSource.current.stop();
      currentPlaybackSource.current.disconnect();
      currentPlaybackSource.current = null;
    }

    // Close the WebSocket connection
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    // Stop all tracks in the microphone stream
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }

    // Disconnect the ScriptProcessorNode
    if (scriptNodeRef.current) {
      scriptNodeRef.current.disconnect();
      scriptNodeRef.current = null;
    }

    // Close the AudioContext
    if (audioCtxRef.current) {
      audioCtxRef.current.close().then(() => {
        audioCtxRef.current = null;
      });
    }

    // Clear any remaining audio in the playback queue
    playbackQueue.current = [];
  };

  /**
   * Toggles the microphone mute state.
   * Also enables/disables the microphone track directly.
   */
  const toggleMute = () => {
    setIsMuted((prev) => {
      const newState = !prev;
      // If mic stream exists, enable/disable its audio tracks based on the new mute state
      if (micStreamRef.current) {
        micStreamRef.current.getAudioTracks().forEach((track) => {
          track.enabled = !newState;
        }); // true if unmuted, false if muted
      }
      return newState;
    });
  };

  useEffect(() => {
    if (enableBackgroundNoise && !backgroundAudioRef.current) {
      const audioEl = new Audio(BACKGROUND_LOOP_URL);
      audioEl.loop = true;
      audioEl.preload = 'auto';
      audioEl.volume = 0.1;
      backgroundAudioRef.current = audioEl;
    }
  }, [enableBackgroundNoise]);

  useEffect(() => {
    return () => {
      stopCall();
    };
  }, []);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  return {
    isMuted,
    isCalling,
    isCallConnected,
    callDuration,
    callError,
    startCall,
    stopCall,
    toggleMute,
  };
}
